import { useEffect, type WheelEvent } from 'react';
import { useSettings } from '@/store/settingStore';
import { cn } from '@/utils';
import { Outlet } from 'react-router';
import MultiTabs from './multi-tabs';
import { MultiTabsProvider } from './multi-tabs/providers/multi-tabs-provider';

const Main = () => {
  const { themeStretch, multiTab } = useSettings();

  useEffect(() => {
    let frame = 0;

    const syncTableCellLabels = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        document.querySelectorAll<HTMLElement>('.ant-table-wrapper table').forEach((table) => {
          const labels = Array.from(table.querySelectorAll<HTMLElement>('thead th')).map(
            (header) => header.textContent?.trim() ?? '',
          );

          table
            .querySelectorAll<HTMLElement>('tbody > tr:not(.ant-table-expanded-row)')
            .forEach((row) => {
              row.querySelectorAll<HTMLElement>(':scope > td').forEach((cell, index) => {
                const label = labels[index];
                if (label) cell.dataset.label = label;
              });
            });
        });
      });
    };

    syncTableCellLabels();

    const observer = new MutationObserver(syncTableCellLabels);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const handleWheelCapture = (event: WheelEvent<HTMLDivElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.closest('.ant-table-wrapper')) return;
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

    event.currentTarget.scrollTop += event.deltaY;
    event.preventDefault();
  };

  return (
    <main
      data-slot="slash-layout-main"
      className={cn('flex min-h-0 w-full grow', {
        'md:pt-[var(--layout-multi-tabs-height)]': multiTab,
      })}
    >
      <div
        data-slot="slash-layout-scroll"
        className={cn(
          'h-full min-h-0 w-full touch-pan-y overflow-x-hidden overflow-y-auto p-4 mx-auto transition-all duration-300 ease-in-out overscroll-contain md:p-5',
          themeStretch ? '' : 'xl:max-w-screen-xl',
        )}
        onWheelCapture={handleWheelCapture}
      >
        {multiTab ? (
          <MultiTabsProvider>
            <MultiTabs />
          </MultiTabsProvider>
        ) : (
          <Outlet />
        )}
      </div>
    </main>
  );
};

export default Main;
